













































































































import {
  defineComponent,
  useRoute,
  ref,
  computed,
  useRouter,
  watch,
  useContext,
  useFetch,
} from '@nuxtjs/composition-api';
import ProductReviewCard from '~/components/pages/Product/ProductReviewCard.vue';
import type { FilterOption } from '~/components/partials/App/AppFilterSelect.vue';
import AppFilterSelect from '~/components/partials/App/AppFilterSelect.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppImage from '~/components/partials/App/AppImage.vue';
import AppLoader from '~/components/partials/App/AppLoader.vue';
import AppSeeMoreButton from '~/components/partials/App/AppSeeMoreButton.vue';
import AppSortSelect from '~/components/partials/App/AppSortSelect.vue';
import GlobalHeaderMenu from '~/components/partials/Global/GlobalHeaderMenu.vue';
import LayoutDefault from '~/components/partials/Layout/LayoutDefault.vue';
import ProducingAreaFlag from '~/components/partials/ProducingArea/ProducingAreaFlag.vue';
import ProductLabel from '~/components/partials/Product/ProductLabel.vue';
import { useComment } from '~/composables/mypage/useComment';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useStore } from '~/composables/useStore';
import { COMMENT_SORT_OPTIONS, COMMENT_PAGES } from '~/constants/comments';
import { WINE_TYPE_MAP } from '~/constants/products';
import { BOTTLE_100 } from '~/constants/profilesForImage';
import { url } from '~/utils';
import { PageError } from '~/utils/error';
import { formatProducingArea, filterReviewRating } from '~/utils/modules/products';
import priceFormatter from '~/utils/priceFormatter';
import { ProductReviews, CommentData } from '~/types/modules/comment';
import { ProductShowECResponseSchema } from '~/types/modules/product';

const ALL = 'ALL';

export default defineComponent({
  components: {
    ProductReviewCard,
    ProducingAreaFlag,
    ProductLabel,
    AppIcon,
    AppLoader,
    AppSeeMoreButton,
    AppFilterSelect,
    AppSortSelect,
    AppImage,
    LayoutDefault,
    GlobalHeaderMenu,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const productCode = computed(() => String(route.value.query.product_code));
    const store = useStore();
    const { i18n } = useContext();

    const hasMoreComments = ref(true);
    const hasUserPostedComment = ref(false);
    const productDetail = ref<ProductShowECResponseSchema | null>(null);
    const comments = ref<ProductReviews | null>(null);
    const commentsFilteredByVintage = ref<CommentData[]>([]);
    const vintageFilterOptions = ref<FilterOption[]>([
      {
        label: i18n.t('Item-17.all.vintages').toString(),
        target: ALL,
        index: 0,
      },
    ]);
    const hasVintageOptions = ref(true);
    const currentFilterIndex = ref(0);
    const isLoading = ref(true);
    let currentPage = 1;
    const { currentSortIndex, currentSortParameter, likedComments, fetchLikedComments, handleLikeChange } = useComment(
      COMMENT_PAGES.PRODUCT
    );

    const isLoggedIn = computed(() => {
      if (!store.state.user.info.userBase) return false;
      return store.state.user.info.userBase.loginId !== '';
    });
    const { fetchState } = useFetch(async () => {
      if (!productCode.value) {
        isLoading.value = false;
        return;
      }
      try {
        productDetail.value = await store.dispatch('modules/product/fetchProductDetail', {
          path: {
            code: productCode.value,
          },
        });
        if (!productDetail.value) {
          isLoading.value = false;
          return;
        }
        const reviewGroupId = productDetail.value?.review_settings?.review_group_id;
        if (!reviewGroupId) throw new Error('Invalid review group ID');

        if (isLoggedIn.value) {
          const userReviews = await store.dispatch('modules/reviews/fetchCommentsByUserId', {
            query: {
              limit: 10,
              page: 1,
              sort: currentSortParameter.value,
              review_group_id: reviewGroupId,
            },
          });
          // 掲載状態・ステータスにかかわらず投稿数が0 または 投稿したレビューが全て非公開の承認されたレビューの場合、投稿可能
          // ↓はその逆のboolean、わかりづらかったら修正
          const approvedPrivateCommentsLength = userReviews.data.filter(
            (review) => !review.display_flag && review.status === 'approved'
          ).length;
          hasUserPostedComment.value =
            userReviews.data.length > 0 ? !!(userReviews.data.length - approvedPrivateCommentsLength) : false;
        }

        comments.value = await store.dispatch('modules/reviews/fetchCommentsByReviewGroupId', {
          path: {
            review_group_id: reviewGroupId,
          },
          query: {
            limit: 10,
            page: 1,
            sort: currentSortParameter.value,
            status: 'approved',
          },
        });
        if (!comments.value || !comments.value.data) return;
        if (commentList.value.length < 10 || comments.value.total === 10) hasMoreComments.value = false;
        addVintageFilterOptions(commentList.value);
        commentsFilteredByVintage.value = commentList.value;
        await fetchLikedComments();
      } catch (err) {
        throw new PageError(404, {});
      } finally {
        isLoading.value = false;
      }
    });
    const addVintageFilterOptions = (comments: CommentData[]): void => {
      for (const comment of comments) {
        const vintage = comment.metadata?.sku;
        if (!vintage) continue;
        if (vintageFilterOptions.value.some((filterOption) => filterOption.target === vintage)) continue;
        const newFilterOption = {
          label: vintage,
          target: vintage,
          index: vintageFilterOptions.value.length,
        };
        vintageFilterOptions.value.push(newFilterOption);
      }
      // 一つ以上のヴィンテージの場合のみ絞り込みを表示
      hasVintageOptions.value = vintageFilterOptions.value.length > 1;
    };
    const totalComments = computed(() => {
      // バッチ集計後の値を参照するため商品詳細の情報を利用
      return productDetail.value?.review_settings?.review_total || 0;
    });
    const averageRating = computed(() => {
      // バッチ集計後の値を参照するため商品詳細の情報を利用
      const average = productDetail.value?.review_settings?.rating_average || 0;
      return Math.round(average * 10) / 10;
    });
    const ratings = computed(() => {
      let ratingCounts = [0, 0, 0, 0, 0];
      if (comments.value) {
        ratingCounts = [
          comments.value.rating_5_count || 0,
          comments.value.rating_4_count || 0,
          comments.value.rating_3_count || 0,
          comments.value.rating_2_count || 0,
          comments.value.rating_1_count || 0,
        ];
      }
      return ratingCounts.map((count: number) => count?.toLocaleString());
    });
    const wineType = computed(() => productDetail.value?.master_settings?.color?.name || '');
    const producerName = computed(() => productDetail.value?.master_settings?.producer?.name_kana || '');
    const producingArea = computed(() => {
      if (productDetail.value?.master_settings?.producing_areas)
        return formatProducingArea(productDetail.value.master_settings.producing_areas);
    });
    const priceIncludingTax = computed(() => {
      if (productDetail.value?.product_variants) {
        const price = productDetail.value.product_variants[0]?.price_settings?.price_including_tax;
        if (price) return priceFormatter(price);
      }
      return '';
    });
    const productName = computed(() => productDetail.value?.langs.find(({ id }) => id === 'ja')?.name || '');
    const commentList = computed(() => {
      return comments.value?.data || [];
    });
    const image = computed(() => {
      return productDetail.value?.main_image_url ? productDetail.value.main_image_url + BOTTLE_100 : undefined;
    });
    const barWidthStyle = (commentCount: string) => {
      if (comments.value?.total)
        return {
          width: `${(Math.min(comments.value.total, parseInt(commentCount, 10)) / comments.value.total) * 100}%`,
        };
    };
    const filterCommentsByVintage = () => {
      const vintage = vintageFilterOptions.value[currentFilterIndex.value].target?.toString() || ALL;
      if (vintage === ALL) {
        commentsFilteredByVintage.value = commentList.value;
        return;
      }
      commentsFilteredByVintage.value = commentList.value.filter((comment) => comment.metadata?.sku === vintage);
    };
    // 表示順が変更された時、今表示されているクチコミ件数と同じ件数を取得
    const fetchComments = async () => {
      const reviewGroupId = productDetail.value?.review_settings?.review_group_id;
      if (!reviewGroupId || !comments.value || !comments.value.data) return;
      // いいねボタンの状態を更新する必要がある
      await fetchLikedComments();
      comments.value = await store.dispatch('modules/reviews/fetchCommentsByReviewGroupId', {
        path: {
          review_group_id: reviewGroupId,
        },
        query: {
          limit: commentList.value.length,
          page: 1,
          sort: currentSortParameter.value,
          status: 'approved',
        },
      });
      if (hasVintageOptions.value) {
        filterCommentsByVintage();
      } else {
        commentsFilteredByVintage.value = commentList.value;
      }
    };
    // もっと見るボタンを押下した時に、後十件を取得
    const loadAdditionalComments = async () => {
      const reviewGroupId = productDetail.value?.review_settings?.review_group_id;
      if (!reviewGroupId || !comments.value || !comments.value.data) return;
      currentPage++;
      const additionalComments = await store.dispatch('modules/reviews/fetchCommentsByReviewGroupId', {
        path: {
          review_group_id: reviewGroupId,
        },
        query: {
          limit: 10,
          page: currentPage,
          sort: currentSortParameter.value,
          status: 'approved',
        },
      });
      if (additionalComments.data) comments.value.data = comments.value.data.concat(additionalComments.data);
      const currentLength = commentList.value.length;
      if (currentLength === additionalComments.total) hasMoreComments.value = false;
      addVintageFilterOptions(commentList.value);
      commentsFilteredByVintage.value = commentList.value;
    };
    const openCommentForm = async () => {
      if (!isLoggedIn.value) {
        await router.push(url('AUTH_LOGIN'));
        return;
      }
      await router.push(url('COMMENT_FORM_NEW', { code: productCode.value }));
    };

    watch(currentFilterIndex, () => filterCommentsByVintage());
    watch(currentSortIndex, () => fetchComments());

    useLayoutProps(() => ({
      layout: {
        header: 'always',
        footer: 'always',
        footerMenu: 'always',
        headerMenu: 'never',
        topBanner: 'never',
      },
      head: {
        title: i18n.t('Item-17.pageTitle', { productName: productName.value }).toString(),
        description: i18n.t('Item-17.description').toString(),
      },
      breadcrumbs: [
        {
          title: i18n.t('Item-03.breadcrumbs.title', { productName: productName.value }).toString(),
          to: { path: url('ITEM_DETAIL', { code: productCode.value }) },
        },
        {
          title: i18n.t('Item-17.breadcrumbs.title', { productName: productName.value }).toString(),
          to: { path: url('COMMENT_LIST'), query: { product_code: productCode.value } },
        },
      ],
      fetchState,
    }));

    return {
      WINE_TYPE_MAP,
      hasVintageOptions,
      vintageFilterOptions,
      COMMENT_SORT_OPTIONS,
      currentSortIndex,
      currentFilterIndex,
      isLoading,
      commentsFilteredByVintage,
      likedComments,
      handleLikeChange,
      hasMoreComments,
      productDetail,
      wineType,
      producerName,
      producingArea,
      priceIncludingTax,
      productName,
      commentList,
      comments,
      hasUserPostedComment,
      totalComments,
      averageRating,
      ratings,
      image,
      barWidthStyle,
      priceFormatter,
      filterCommentsByVintage,
      loadAdditionalComments,
      openCommentForm,
      filterReviewRating,
    };
  },
  head: {},
});
